import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionCreator } from 'redux';
import { TOperationStatus } from '../../types/redux-operation';
import TStore from '../../types/store';

function empty(_: TStore): undefined {
  return undefined;
}

type TUseOperationStatus<S, E> = {
  stateGetter: (state: TStore) => S;
  errorGetter?: (state: TStore) => E | undefined;
  resetAction?: ActionCreator<any>;
  onSuccess?: () => void;
  onError?: (error?: any) => void;
};

/**
 * Hook to listen to redux operation status.
 * If reset action provided, it will be fired on mount/un-mount.
 * onSuccess/onError callbacks will be fired on status change
 * @param param0 params
 * @returns status from getter
 */
function useOperationStatus<S extends TOperationStatus | undefined, E extends any>({
  stateGetter,
  errorGetter = empty,
  resetAction,
  onSuccess,
  onError,
}: TUseOperationStatus<S, E>) {
  const dispatch = useDispatch();
  const status = useSelector<TStore, TOperationStatus | undefined>(stateGetter);
  const error = useSelector<TStore, ReturnType<typeof errorGetter>>(errorGetter);
  const fired = React.useRef(false);

  /**
   * Reset status on mount/un-mount
   */
  React.useEffect(() => {
    if (resetAction) {
      dispatch(resetAction?.());
    }
    return () => {
      if (resetAction) {
        dispatch(resetAction?.());
      }
    };
  }, [dispatch, resetAction]);

  /**
   * Listen to status changes and fire callbacks
   */
  React.useEffect(() => {
    if (status === 'success' && !fired.current) {
      onSuccess?.();
      fired.current = true;
    } else if (status === 'error' && !fired.current) {
      onError?.(error);
      fired.current = true;
    } else {
      fired.current = false;
    }
  }, [status, fired]);

  /**
   * Return current status
   */
  return { status, error };
}

export default useOperationStatus;
